// Pure-state hook for a polygon-in-progress. MapPage owns all map click events
// and calls appendVertex() / finish() / clear(). Same pattern as useSegmentDraw.
//
// Why not attach a click handler here? Because if the map isn't loaded on
// first render (mapRef.current is null), the effect's early-return leaves us
// with no listener attached forever.
import { useCallback, useEffect, useRef, useState } from 'react';
export function usePolygonDraw(mapRef, opts) {
    const [ring, setRing] = useState([]);
    const ringRef = useRef([]);
    const onChangeRef = useRef(opts.onChange);
    const onFinishRef = useRef(opts.onFinish);
    onChangeRef.current = opts.onChange;
    onFinishRef.current = opts.onFinish;
    const push = useCallback((next) => {
        ringRef.current = next;
        setRing(next);
        onChangeRef.current?.(next);
    }, []);
    const appendVertex = useCallback((ll) => push([...ringRef.current, ll]), [push]);
    const finish = useCallback(() => {
        if (ringRef.current.length >= 3) {
            const closed = [...ringRef.current, ringRef.current[0]];
            onFinishRef.current?.(closed);
        }
        push([]);
    }, [push]);
    const clear = useCallback(() => push([]), [push]);
    const undo = useCallback(() => {
        if (ringRef.current.length)
            push(ringRef.current.slice(0, -1));
    }, [push]);
    // Keybindings.
    useEffect(() => {
        if (!opts.active)
            return;
        const onKey = (e) => {
            if (e.key === 'Escape')
                clear();
            else if ((e.key === 'Backspace' || e.key === 'z') && (e.metaKey || e.ctrlKey))
                undo();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [opts.active, clear, undo]);
    // Exiting mode clears the draft.
    useEffect(() => {
        if (!opts.active && ringRef.current.length)
            push([]);
    }, [opts.active, push]);
    // Cursor.
    useEffect(() => {
        const map = mapRef.current;
        if (!map)
            return;
        if (opts.active)
            map.getCanvas().style.cursor = 'crosshair';
        return () => { if (map)
            map.getCanvas().style.cursor = ''; };
    }, [opts.active, mapRef]);
    return { ring, appendVertex, finish, clear, undo };
}
